import React, { useEffect, useMemo } from 'react';
import {
	Box,
	Button,
	FormControl,
	FormLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Select,
	Stack,
	Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { type Repository } from '../types';
import { isChartDateLikePropertyType } from '../chart/canvasChartData';
import { useGetRepositoryByIdQuery } from '../services/repositoryEntries';
import { useAppSelector } from '../hooks';
import { selectIsAuthenticated } from '../containers/authSlice';
import { selectAllPropertyDefinitions } from '../containers/contentSlice';

export interface TimelineCollectionInput {
	/** Present for an existing timeline collection row being kept/edited; absent for a newly added one. */
	id?: string;
	repositoryId: string;
	datePropertyId: string;
}

export function isTimelineCollectionInputComplete(input: TimelineCollectionInput): boolean {
	return Boolean(input.repositoryId && input.datePropertyId);
}

interface TimelineCollectionRowProps {
	index: number;
	value: TimelineCollectionInput;
	repositories: Repository[];
	onChange: (index: number, next: TimelineCollectionInput) => void;
	onRemove: (index: number) => void;
}

function TimelineCollectionRow({ index, value, repositories, onChange, onRemove }: TimelineCollectionRowProps) {
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const { data: repositoryById } = useGetRepositoryByIdQuery(value.repositoryId, {
		skip: !value.repositoryId || !isAuthenticated,
	});

	const dateOptions = useMemo(() => {
		if (!value.repositoryId) return [];
		const definitions = isAuthenticated ? (repositoryById?.propertyDefinitions ?? []) : allPropertyDefinitions;
		return definitions.filter((d) => d.parentId === value.repositoryId && isChartDateLikePropertyType(d.type));
	}, [value.repositoryId, isAuthenticated, repositoryById?.propertyDefinitions, allPropertyDefinitions]);

	useEffect(() => {
		if (dateOptions.length === 0 || dateOptions.some((d) => d.id === value.datePropertyId)) return;
		onChange(index, { ...value, datePropertyId: dateOptions[0].id });
	}, [dateOptions]);

	return (
		<Box display="flex" flexDirection="column" gap={0.5}>
			<Stack direction="row" gap={1} alignItems="center">
				<FormControl fullWidth size="small" required>
					<InputLabel id={`timeline-collection-repository-label-${index}`}>Collection</InputLabel>
					<Select
						labelId={`timeline-collection-repository-label-${index}`}
						label="Collection"
						value={value.repositoryId}
						onChange={(e) => onChange(index, { ...value, repositoryId: String(e.target.value), datePropertyId: '' })}
					>
						{repositories.map((r) => (
							<MenuItem key={`timeline-collection-repo-${r.id}`} value={r.id}>
								{r.title}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<FormControl fullWidth size="small" required disabled={!value.repositoryId || dateOptions.length === 0}>
					<InputLabel id={`timeline-collection-date-property-label-${index}`}>Date field</InputLabel>
					<Select
						labelId={`timeline-collection-date-property-label-${index}`}
						label="Date field"
						value={dateOptions.some((d) => d.id === value.datePropertyId) ? value.datePropertyId : ''}
						onChange={(e) => onChange(index, { ...value, datePropertyId: String(e.target.value) })}
					>
						{dateOptions.map((d) => (
							<MenuItem key={`timeline-collection-date-prop-${d.id}`} value={d.id}>
								{d.label}
							</MenuItem>
						))}
					</Select>
				</FormControl>
				<IconButton size="small" onClick={() => onRemove(index)} aria-label="Remove collection">
					<DeleteOutlineIcon fontSize="small" />
				</IconButton>
			</Stack>
			{value.repositoryId && (!isAuthenticated || repositoryById) && dateOptions.length === 0 && (
				<Typography variant="caption" color="text.secondary">
					This collection has no date or datetime field yet.
				</Typography>
			)}
		</Box>
	);
}

export interface TimelineCollectionsFieldProps {
	value: TimelineCollectionInput[];
	repositories: Repository[];
	onChange: (next: TimelineCollectionInput[]) => void;
}

const TimelineCollectionsField: React.FC<TimelineCollectionsFieldProps> = ({ value, repositories, onChange }) => {
	const handleRowChange = (index: number, next: TimelineCollectionInput) => {
		onChange(value.map((row, i) => (i === index ? next : row)));
	};

	const handleRemove = (index: number) => {
		onChange(value.filter((_, i) => i !== index));
	};

	const handleAdd = () => {
		onChange([...value, { repositoryId: '', datePropertyId: '' }]);
	};

	return (
		<Box display="flex" flexDirection="column" gap={1}>
			<FormLabel component="legend" sx={{ typography: 'body2', color: 'text.primary' }}>
				Collections
			</FormLabel>
			{value.length === 0 && (
				<Typography variant="body2" sx={{ color: 'text.secondary' }}>
					No collections yet. Add one to lay its entries out by day.
				</Typography>
			)}
			<Stack gap={1.5}>
				{value.map((row, index) => (
					<TimelineCollectionRow
						key={`timeline-collection-${index}`}
						index={index}
						value={row}
						repositories={repositories.filter(
							(r) => r.id === row.repositoryId || !value.some((other) => other.repositoryId === r.id),
						)}
						onChange={handleRowChange}
						onRemove={handleRemove}
					/>
				))}
			</Stack>
			<Button type="button" size="small" startIcon={<AddIcon />} onClick={handleAdd} sx={{ alignSelf: 'flex-start' }}>
				Add collection
			</Button>
		</Box>
	);
};

export default TimelineCollectionsField;
